import { Component, OnDestroy, OnInit, effect, signal, ChangeDetectionStrategy, inject } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import {
  CommonHelper,
  Notification,
  NotificationMetadata,
  NotificationType,
  PaginationOptions,
} from '@meplis/common';
import { AppRoutes } from '@cnp/common';
import { NotificationWebsocketService } from '@meplis/services';
import { ApiNotificationsService } from '@cnp/services';
import { NotificationService } from '@cnp/services/notification';
import { Subscription } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FaIconNamePipe } from '@cnp/pipes';

@Component({
  selector: 'app-notification-menu',
  standalone: true,
  imports: [MatButtonModule, MatMenuModule, TranslatePipe, FaIconComponent, FaIconNamePipe],
  templateUrl: './notification-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./notification-menu.component.scss'],
})
export class NotificationMenuComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _notificationService = inject(NotificationService);
  private _apiNotificationsService = inject(ApiNotificationsService);
  private _notificationWebsocketService = inject(NotificationWebsocketService);

  public pagination = signal<PaginationOptions>({
    page: 0,
    limit: 6,
  });

  public isLoading = signal(false);
  public allLoaded = signal<boolean | undefined>(undefined);
  public items = signal<Notification[]>([]);

  private notificationsSubscription: Subscription;
  private _websocketSubscription: Subscription;

  public get displayedNotifications(): Notification[] {
    return this.items();
  }

  constructor() {
    effect(() => {
      this._getItems();
    });
  }

  public get hasNotifications(): boolean {
    return this.displayedNotifications.length > 0;
  }

  public get badgeCount(): number {
    return this.displayedNotifications.filter(
      (notification) => notification.read !== true
    ).length;
  }

  public ngOnInit(): void {
    this._websocketSubscription =
      this._notificationWebsocketService.notifications$.subscribe(
        (notification) => this._prependNotification(notification)
      );
  }

  public onShowAll(): void {
    this._router.navigate([AppRoutes['MY_NOTIFICATIONS'].fullPath]);
  }

  public onMenuOpened(): void {
    // Matches `badgeCount`'s `!== true` check, not `=== false`: a
    // websocket-delivered notification has no `read` field at all (it's
    // `undefined`), so `=== false` never recognized it as unread and this
    // guard skipped marking anything read whenever realtime items were the
    // only unread ones -- exactly what left the badge stuck on.
    const hasUnread = this.displayedNotifications.some((n) => n.read !== true);
    if (!hasUnread) return;

    // The menu is open now, so treat everything as read immediately rather
    // than waiting on the mark-all-read round trip -- same fix as chat's
    // unread badge: the backend can lag behind what the user just saw, and
    // gating the local state on its response just reintroduces that flicker.
    this.items.update((notifications) =>
      notifications.map((n) => ({ ...n, read: true }))
    );

    this._apiNotificationsService.readAllNotifications().subscribe({
      error: (error) => {
        console.warn(
          '[NotificationMenuComponent] Error marking notifications as read',
          error
        );
      },
    });
  }

  public ngOnDestroy(): void {
    this.notificationsSubscription?.unsubscribe();
    this._websocketSubscription?.unsubscribe();
  }

  public getTruncatedDescription(description: string): string {
    if (!description) {
      return '';
    }

    const maxLength = 20;
    return description.length <= maxLength
      ? description
      : `${description.slice(0, maxLength).trim()}...`;
  }

  // private _buildMockNotifications(): NotificationMenuItem[] {
  //   return [
  //     {
  //       type: NotificationType.CNP_START_FOLLOW_YOU,
  //       config:
  //         NotificationConfigurationConstant[
  //           NotificationType.CNP_START_FOLLOW_YOU
  //         ],
  //       image:
  //         'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80',
  //       data: {
  //         hubId: 'follow-hub',
  //         hubSlug: 'follow-hub',
  //       },
  //       isRead: false,
  //     },
  //     {
  //       type: NotificationType.CNP_REACT_TO_THREAD,
  //       config:
  //         NotificationConfigurationConstant[
  //           NotificationType.CNP_REACT_TO_THREAD
  //         ],
  //       image:
  //         'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=400&q=80',
  //       data: {
  //         threadId: 'thread-reaction-001',
  //       },
  //       isRead: false,
  //     },
  //     {
  //       type: NotificationType.CNP_COMMENT_IN_THREAD,
  //       config:
  //         NotificationConfigurationConstant[
  //           NotificationType.CNP_COMMENT_IN_THREAD
  //         ],
  //       image:
  //         'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=400&q=80',
  //       data: {
  //         threadId: 'thread-comment-002',
  //       },
  //       isRead: true,
  //     },
  //     {
  //       type: NotificationType.CNP_VOTE_IN_CASE,
  //       config:
  //         NotificationConfigurationConstant[NotificationType.CNP_VOTE_IN_CASE],
  //       image:
  //         'https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&w=400&q=80',
  //       data: {
  //         hubId: 'clinical-hub',
  //         hubSlug: 'clinical-hub',
  //         caseId: 'case-204',
  //       },
  //       isRead: false,
  //     },
  //   ];
  // }

  private _getItems(): void {
    if (!this.allLoaded()) {
      this.isLoading.set(true);
      this.notificationsSubscription?.unsubscribe();
      this.notificationsSubscription = this._notificationService
        .getNotifications(this.pagination())
        .subscribe({
          next: (notifications) => {
            this.items.update((prevItems) => [...prevItems, ...notifications]);
            this.isLoading.set(false);

            if (notifications.length < this.pagination().limit) {
              this.allLoaded.set(true);
            }
          },
          error: (error) => {
            this.isLoading.set(false);
            this.notificationsSubscription?.unsubscribe();
            console.warn(
              '[NotificationMenuComponent] Error fetching notifications',
              error
            );
          },
        });
    }
  }

  private _prependNotification(notification: Notification): void {
    this.items.update((prevItems) => [notification, ...prevItems]);
  }

  public onNotificationClick(notification: Notification): void {
    const routeUrl = this.buildNotificationRoute(notification.metadata);
    if (!routeUrl) {
      return;
    }

    // Whatever the notification type, a commentId means it points at one
    // comment inside the thread the route above already resolves to -- the
    // thread page reads this back to scroll to and highlight it.
    const commentId = notification.metadata?.commentId;
    this._router.navigate(
      [routeUrl],
      commentId ? { queryParams: { commentId } } : undefined
    );
  }

  public trackByNotification(
    index: number,
    notification: Notification
  ): string {
    return notification.token ?? `${index}-${notification.metadata?.type}`;
  }

  public resetList(): void {
    this.allLoaded.set(undefined);
    this.items.set([]);
    this.pagination.set({
      ...this.pagination(),
      page: 0,
    });
  }

  public loadMore(): void {
    if (!this.isLoading()) {
      this.pagination.set({
        ...this.pagination(),
        page: this.pagination().page + 1,
      });
    }
  }

  private buildNotificationRoute(data?: NotificationMetadata): string | null {
    if (!data) {
      return null;
    }

    if (data.type === NotificationType.CNP_START_FOLLOW_YOU) {
      return AppRoutes['MY_FOLLOWERS'].fullPath;
    }

    if (data.threadId) {
      return CommonHelper.buildFullRouteUrl(AppRoutes['THREAD'].fullPath, [
        data.threadId,
      ]);
    }

    const hubSlug = data?.hubSlug || data?.hubId || '';

    if (data.videoId) {
      return data.hubId
        ? CommonHelper.buildFullRouteUrl(AppRoutes['HUB_VIDEO'].fullPath, [
            hubSlug,
            data.hubId,
            data.videoId,
          ])
        : CommonHelper.buildFullRouteUrl(AppRoutes['VIDEO'].fullPath, [
            data.videoId,
          ]);
    }

    if (data.eventId) {
      return data.hubId
        ? CommonHelper.buildFullRouteUrl(AppRoutes['HUB_EVENT'].fullPath, [
            hubSlug,
            data.hubId,
            data.eventSlug || data.eventId,
            data.eventId,
          ])
        : CommonHelper.buildFullRouteUrl(AppRoutes['EVENT'].fullPath, [
            data.eventId,
          ]);
    }

    if (data.hubId && data.groupId && data.caseId) {
      return CommonHelper.buildFullRouteUrl(
        AppRoutes['HUB_GROUP_CLINICAL_CASE'].fullPath,
        [hubSlug, data.hubId, data.groupId, data.caseId]
      );
    }

    if (data.hubId && data.caseId) {
      return CommonHelper.buildFullRouteUrl(
        AppRoutes['HUB_CLINICAL_CASE'].fullPath,
        [hubSlug, data.hubId, data.caseId]
      );
    }

    if (data.hubId && data.groupId) {
      return CommonHelper.buildFullRouteUrl(AppRoutes['HUB_GROUP'].fullPath, [
        hubSlug,
        data.hubId,
        data.groupId,
      ]);
    }

    if (data.hubId) {
      return CommonHelper.buildFullRouteUrl(AppRoutes['HUB'].fullPath, [
        hubSlug,
        data.hubId,
      ]);
    }

    // Standalone group (CNP_NEW_MEMBER_JOINED / CNP_NEW_CONTENT_PUBLISHED
    // without threadId, e.g. a member-join notification for a group that
    // isn't nested under a hub).
    if (data.groupId) {
      return CommonHelper.buildFullRouteUrl(AppRoutes['GROUP'].fullPath, [
        data.groupId,
      ]);
    }

    return null;
  }
}
